
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchHeader } from "@/components/routines/search/SearchHeader";
import { RoutinesList } from "@/components/routines/search/RoutinesList";
import { PlansList } from "@/components/routines/search/PlansList";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface VisibilityRecord {
  content_type: string;
  content_id: string;
  visibility_type: string;
  created_by: string;
  content_visibility_members: { user_id: string }[];
}

const BuscarRutinas = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const { profile } = useProfile();
  const queryClient = useQueryClient();
  const [selectedProfessorId, setSelectedProfessorId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("plans");

  const { data: allProfessors } = useQuery({
    queryKey: ["gym-professors-search", profile?.gym_id],
    queryFn: async () => {
      if (!profile?.gym_id) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .in("role", ["profesor", "manager"])
        .eq("gym_id", profile.gym_id)
        .order("first_name", { ascending: true });
      if (error) throw error;
      return (data || []).filter((p) => p.first_name);
    },
    enabled: !!profile?.gym_id,
  });

  const { data: routines, isLoading: isLoadingRoutines } = useQuery({
    queryKey: ["public-routines", profile?.gym_id],
    queryFn: async () => {
      if (!profile?.gym_id) return [];

      // Get professors from the same gym
      const { data: professors, error: professorsError } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .in("role", ["profesor", "manager"])
        .eq("gym_id", profile.gym_id);

      if (professorsError) {
        console.error("Error fetching professors:", professorsError);
        throw professorsError;
      }

      // Get their public routines
      if (professors && professors.length > 0) {
        const professorIds = professors.map(prof => prof.id);
        const { data: routines, error: routinesError } = await supabase
          .from("routines")
          .select(`
            *,
            exercises (*),
            profiles!inner (
              first_name,
              last_name,
              role,
              gym_id
            )
          `)
          .in("user_id", professorIds);

        if (routinesError) {
          console.error("Error fetching routines:", routinesError);
          throw routinesError;
        }

        console.log("Fetched public routines:", routines);
        return routines;
      }

      return [];
    },
    enabled: !!profile?.gym_id,
  });

  const { data: workoutPlans, isLoading: isLoadingPlans } = useQuery({
    queryKey: ["public-workout-plans", profile?.gym_id],
    queryFn: async () => {
      if (!profile?.gym_id) return [];

      const { data: plans, error } = await supabase
        .from("workout_plans")
        .select(`
          *,
          profiles!inner (
            first_name,
            last_name,
            role,
            gym_id
          ),
          workout_plan_routines (
            routines (
              id,
              name,
              exercises (*)
            )
          )
        `)
        .in("profiles.role", ["profesor", "manager"])
        .eq("profiles.gym_id", profile.gym_id)
        .eq("is_personal", false);

      if (error) {
        console.error("Error fetching workout plans:", error);
        throw error;
      }

      console.log("Fetched workout plans:", plans);
      return plans;
    },
    enabled: !!profile?.gym_id,
  });

  // Fetch visibility settings for the gym
  const { data: visibilityData } = useQuery({
    queryKey: ["content-visibility", profile?.gym_id],
    queryFn: async () => {
      if (!profile?.gym_id) return [];
      const { data, error } = await supabase
        .from("content_visibility")
        .select("content_type, content_id, visibility_type, created_by, content_visibility_members(user_id)")
        .eq("gym_id", profile.gym_id);

      if (error) {
        console.error("Error fetching visibility:", error);
        throw error;
      }
      return (data || []) as VisibilityRecord[];
    },
    enabled: !!profile?.gym_id,
  });

  const isVisibleToUser = (contentType: string, contentId: string, creatorId: string) => {
    // Creator always sees their own content
    if (creatorId === profile?.id) return true;
    
    const vis = visibilityData?.find(
      v => v.content_type === contentType && v.content_id === contentId
    );
    // No visibility record = visible to all (default)
    if (!vis) return true;
    if (vis.visibility_type === "all") return true;
    // Specific: check if current user is in the members list
    return vis.content_visibility_members?.some(m => m.user_id === profile?.id) || false;
  };

  const filteredRoutines = routines
    ?.filter(routine => routine.name.toLowerCase().includes(searchQuery.toLowerCase()))
    ?.filter(routine => isVisibleToUser("routine", routine.id, routine.user_id))
    ?.filter(routine => !selectedProfessorId || routine.user_id === selectedProfessorId);

  const filteredPlans = workoutPlans
    ?.filter(plan => plan.name.toLowerCase().includes(searchQuery.toLowerCase()))
    ?.filter(plan => isVisibleToUser("workout_plan", plan.id, plan.created_by))
    ?.filter(plan => !selectedProfessorId || plan.created_by === selectedProfessorId);

  const professors = (() => {
    const ids = new Set<string>();
    routines?.forEach((r: any) => r?.user_id && ids.add(r.user_id));
    workoutPlans?.forEach((p: any) => p?.created_by && ids.add(p.created_by));
    return (allProfessors || []).filter((p) => ids.has(p.id));
  })();

  const getInstructorName = (item: any) => {
    if (!item?.profiles) return "Profesor no disponible";
    const { first_name, last_name } = item.profiles;
    if (!first_name && !last_name) return "Profesor no disponible";
    return `${first_name || ''} ${last_name || ''}`.trim();
  };

  return (
    <div className="min-h-screen bg-background text-primary">
      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        <SearchHeader 
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showInfoButton={activeTab === "routines"}
        />

        {professors && professors.length > 0 && (
          <div className="max-w-xl mx-auto mb-6 sm:mb-8 flex flex-wrap gap-2 justify-center">
            <Button
              type="button"
              size="sm"
              variant={selectedProfessorId === null ? "default" : "outline"}
              onClick={() => setSelectedProfessorId(null)}
              className="rounded-full"
            >
              Todos
            </Button>
            {professors.map((prof) => {
              const isActive = selectedProfessorId === prof.id;
              return (
                <Button
                  key={prof.id}
                  type="button"
                  size="sm"
                  variant={isActive ? "default" : "outline"}
                  onClick={() => setSelectedProfessorId(isActive ? null : prof.id)}
                  className={cn("rounded-full")}
                >
                  {prof.first_name}
                </Button>
              );
            })}
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 h-auto sm:h-10 bg-muted">
            <TabsTrigger 
              value="plans"
              className="text-sm sm:text-base py-2 sm:py-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:text-primary"
            >
              Planes
            </TabsTrigger>
            <TabsTrigger 
              value="routines" 
              className="text-sm sm:text-base py-2 sm:py-0 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground hover:text-primary"
            >
              Rutinas
            </TabsTrigger>
          </TabsList>

          <TabsContent value="routines">
            <RoutinesList
              routines={filteredRoutines || []}
              isLoading={isLoadingRoutines}
              getInstructorName={getInstructorName}
              onRoutineDeleted={() => queryClient.invalidateQueries({ queryKey: ["public-routines"] })}
            />
          </TabsContent>

          <TabsContent value="plans">
            <PlansList
              plans={filteredPlans || []}
              isLoading={isLoadingPlans}
              getInstructorName={getInstructorName}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default BuscarRutinas;
